import BankAccountApplicationForm from '@/components/bankAccountApplications/form/BankAccountApplicationForm';
import { SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { ROUTES } from '@/constants/routes';
import { useGetBankApplicationDetails } from '@/hooks/requests/useBankApplications';
import {
  BankApplicationAccountType,
  BankApplicationStatus,
} from '@/types/bankApplications';
import {
  getBankAccountApplicationTitle,
  getBankApplicationAccountStatusLabel,
} from '@/utils/bankAccountApplications';
import { Card, Typography } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';

export default function BankAccountEditApplicationPage() {
  const { uuid = '' } = useParams();
  const { bankApplication, isBankApplicationLoading } =
    useGetBankApplicationDetails(uuid);

  if (isBankApplicationLoading) {
    return (
      <DefaultLayout header="Edit Bank Account Application">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!bankApplication) {
    return (
      <DefaultLayout header="Edit Bank Account Application">
        <Card>
          <Typography>
            Something went wrong retreiving the bank account application. Our
            team has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const {
    primaryOwner,
    business,
    status,
    accountType,
    advisor,
    owners,
    beneficiaries,
    withdrawFrom,
    depositAmount,
  } = bankApplication;
  const header = getBankAccountApplicationTitle({
    fullName: primaryOwner ? primaryOwner.consumer.fullName : '',
    email: primaryOwner ? primaryOwner.consumer.email : '',
    businessName: business ? business.name : '',
  });
  const isJointAccount = accountType === BankApplicationAccountType.JOINT;
  const isIndividualAccount =
    accountType === BankApplicationAccountType.INDIVIDUAL;

  if (
    status !== BankApplicationStatus.DRAFT &&
    status !== BankApplicationStatus.NEED_REVIEW
  ) {
    return <Navigate to={ROUTES.BANK_ACCOUNT_APPLICATIONS_DRAFT} replace />;
  }

  return (
    <DefaultLayout
      header={header}
      breadcrumb={[
        {
          label: `Bank Account Application - ${getBankApplicationAccountStatusLabel(status)}`,
          href: ROUTES.BANK_ACCOUNT_APPLICATIONS,
        },
        { label: header, isActive: true },
      ]}>
      <BankAccountApplicationForm
        defaultValues={{
          applicationId: uuid,
          accountType: accountType,
          companyId: advisor.company.uuid,
          advisorId: advisor.uuid,
          individualOwnerId:
            isIndividualAccount && primaryOwner
              ? primaryOwner.consumer.uuid
              : '',
          jointOwnerIds: isJointAccount
            ? owners.map(owner => {
                return owner.consumer.uuid;
              })
            : [],
          businessId: business?.uuid || '',
          primaryOwner: primaryOwner ? primaryOwner.consumer.uuid : '',
          beneficiaries: beneficiaries,
          externalAccountDepositId: withdrawFrom ? withdrawFrom.uuid : '',
          depositAmount: depositAmount ? depositAmount.value.toString() : '',
          verified: false,
          verifiedExternalAccount: false,
        }}
      />
    </DefaultLayout>
  );
}
